import { Component, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

export type M3CardVariant = 'elevated' | 'filled' | 'outlined';
export type M3CardSize = 'sm' | 'default' | 'lg';

@Component({
  selector: 'app-m3-card',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      [class]="cardClass()"
      [attr.role]="clickable() ? 'button' : null"
      [attr.tabindex]="clickable() ? '0' : null"
      [attr.aria-label]="clickable() ? ariaLabel() : null"
      [attr.aria-describedby]="clickable() ? describedBy() : null"
      [attr.aria-pressed]="clickable() ? pressed() : null"
      [attr.aria-expanded]="clickable() ? expanded() : null"
      [attr.aria-disabled]="clickable() ? disabled() : null"
      (click)="clickable() ? handleClick($event) : null"
      (keydown)="clickable() ? handleKeydown($event) : null"
      (focus)="clickable() ? focused.emit() : null"
      (blur)="clickable() ? blurred.emit() : null"
    >
      <ng-content></ng-content>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      .m3-card {
        position: relative;
        display: flex;
        flex-direction: column;
        border-radius: var(--md-comp-card-shape);
        transition: all var(--md-sys-motion-expressive-effects-fast);
        overflow: hidden;
        box-sizing: border-box;
        cursor: default;

        // Only apply focus styles to clickable cards
        &.clickable:focus-visible {
          outline: var(--md-sys-state-focus-indicator-thickness) solid
            var(--md-sys-color-secondary);
          outline-offset: var(--md-sys-state-focus-indicator-outer-offset);
        }

        &.clickable:focus:not(:focus-visible) {
          outline: none;
        }

        // Size variants
        &.size-sm {
          padding: 0.75rem;
          gap: 0.5rem;
        }

        &.size-default {
          padding: 1rem;
          gap: 0.75rem;
        }

        &.size-lg {
          padding: 1.5rem;
          gap: 1rem;
        }

        // Elevated variant
        &.variant-elevated {
          background: var(--md-sys-color-surface-container-low);
          box-shadow: var(--md-sys-elevation-level1);
          border: none;

          // Only apply interactive states to clickable cards
          &.clickable:hover:not(.disabled) {
            box-shadow: var(--md-sys-elevation-level2);

            &::before {
              content: '';
              position: absolute;
              inset: 0;
              background: var(--md-sys-color-on-surface);
              opacity: var(--md-sys-state-hover-state-layer-opacity);
              pointer-events: none;
            }
          }

          &.clickable:focus:not(.disabled) {
            box-shadow: var(--md-sys-elevation-level1);

            &::before {
              content: '';
              position: absolute;
              inset: 0;
              background: var(--md-sys-color-on-surface);
              opacity: var(--md-sys-state-focus-state-layer-opacity);
              pointer-events: none;
            }
          }

          &.clickable:active:not(.disabled) {
            box-shadow: var(--md-sys-elevation-level1);

            &::before {
              content: '';
              position: absolute;
              inset: 0;
              background: var(--md-sys-color-on-surface);
              opacity: var(--md-sys-state-pressed-state-layer-opacity);
              pointer-events: none;
            }
          }

          &.clickable.dragged {
            box-shadow: var(--md-sys-elevation-level4);

            &::before {
              content: '';
              position: absolute;
              inset: 0;
              background: var(--md-sys-color-on-surface);
              opacity: var(--md-sys-state-dragged-state-layer-opacity);
              pointer-events: none;
            }
          }

          &.disabled {
            background: var(--md-sys-color-surface);
            box-shadow: var(--md-sys-elevation-level1);
            opacity: 0.38;
            cursor: not-allowed;
          }
        }

        // Filled variant
        &.variant-filled {
          background: var(--md-sys-color-surface-container);
          border: none;
          box-shadow: var(
            --md-sys-elevation-level0
          ); /* No elevation by default */

          // Only apply interactive states to clickable cards
          &.clickable:hover:not(.disabled) {
            box-shadow: var(--md-sys-elevation-level1);

            &::before {
              content: '';
              position: absolute;
              inset: 0;
              background: var(--md-sys-color-on-surface);
              opacity: var(--md-sys-state-hover-state-layer-opacity);
              pointer-events: none;
            }
          }

          &.clickable:focus:not(.disabled) {
            box-shadow: var(
              --md-sys-elevation-level0
            ); /* No elevation on focus */

            &::before {
              content: '';
              position: absolute;
              inset: 0;
              background: var(--md-sys-color-on-surface);
              opacity: var(--md-sys-state-focus-state-layer-opacity);
              pointer-events: none;
            }
          }

          &.clickable:active:not(.disabled) {
            box-shadow: var(
              --md-sys-elevation-level0
            ); /* No elevation on press */

            &::before {
              content: '';
              position: absolute;
              inset: 0;
              background: var(--md-sys-color-on-surface);
              opacity: var(--md-sys-state-pressed-state-layer-opacity);
              pointer-events: none;
            }
          }

          &.clickable.dragged {
            box-shadow: var(
              --md-sys-elevation-level3
            ); /* Level 3 elevation when dragged */

            &::before {
              content: '';
              position: absolute;
              inset: 0;
              background: var(--md-sys-color-on-surface);
              opacity: var(--md-sys-state-dragged-state-layer-opacity);
              pointer-events: none;
            }
          }

          &.disabled {
            background: var(--md-sys-color-surface-variant);
            box-shadow: var(
              --md-sys-elevation-level0
            ); /* No elevation when disabled */
            opacity: 0.38;
            cursor: not-allowed;
          }
        }

        // Outlined variant
        &.variant-outlined {
          background: var(--md-sys-color-surface);
          border: 1px solid var(--md-sys-color-outline-variant);
          box-shadow: var(
            --md-sys-elevation-level0
          ); /* No elevation by default */

          // Only apply interactive states to clickable cards
          &.clickable:hover:not(.disabled) {
            box-shadow: var(--md-sys-elevation-level1);
            border-color: var(--md-sys-color-outline-variant);

            &::before {
              content: '';
              position: absolute;
              inset: 0;
              background: var(--md-sys-color-on-surface);
              opacity: var(--md-sys-state-hover-state-layer-opacity);
              pointer-events: none;
            }
          }

          &.clickable:focus:not(.disabled) {
            box-shadow: var(
              --md-sys-elevation-level0
            ); /* No elevation on focus */
            border-color: var(--md-sys-color-on-surface);

            &::before {
              content: '';
              position: absolute;
              inset: 0;
              background: var(--md-sys-color-on-surface);
              opacity: var(--md-sys-state-focus-state-layer-opacity);
              pointer-events: none;
            }
          }

          &.clickable:active:not(.disabled) {
            box-shadow: var(
              --md-sys-elevation-level0
            ); /* No elevation on press */
            border-color: var(--md-sys-color-outline-variant);

            &::before {
              content: '';
              position: absolute;
              inset: 0;
              background: var(--md-sys-color-on-surface);
              opacity: var(--md-sys-state-pressed-state-layer-opacity);
              pointer-events: none;
            }
          }

          &.clickable.dragged {
            box-shadow: var(
              --md-sys-elevation-level3
            ); /* Level 3 elevation when dragged */
            border-color: var(--md-sys-color-outline-variant);

            &::before {
              content: '';
              position: absolute;
              inset: 0;
              background: var(--md-sys-color-on-surface);
              opacity: var(--md-sys-state-dragged-state-layer-opacity);
              pointer-events: none;
            }
          }

          &.disabled {
            background: var(--md-sys-color-surface);
            border-color: rgba(from var(--md-sys-color-outline) r g b / 0.12);
            box-shadow: var(
              --md-sys-elevation-level0
            ); /* No elevation when disabled */
            opacity: 0.38;
            cursor: not-allowed;
          }
        }

        // Clickable state
        &.clickable {
          cursor: pointer;
        }

        // Pressed state
        &.pressed {
          transform: scale(0.98);
        }
      }
    `,
  ],
})
export class M3CardComponent {
  variant = input<M3CardVariant>('elevated');
  size = input<M3CardSize>('default');
  disabled = input<boolean>(false);
  pressed = input<boolean>(false);
  expanded = input<boolean>(false);
  clickable = input<boolean>(false);
  dragged = input<boolean>(false);

  ariaLabel = input<string>();
  describedBy = input<string>();

  clicked = output<MouseEvent>();
  focused = output<void>();
  blurred = output<void>();

  // Internal state for managing click animation
  private isClicking = signal(false);

  handleClick(event: MouseEvent): void {
    if (!this.disabled() && this.clickable()) {
      // Set clicking state for animation
      this.isClicking.set(true);

      // Emit the click event
      this.clicked.emit(event);

      // Reset the clicking state after a short delay to allow animation
      setTimeout(() => {
        this.isClicking.set(false);
        // Also blur the element to remove any remaining focus state
        if (event.target instanceof HTMLElement) {
          event.target.blur();
        }
      }, 150); // Match the animation duration
    }
  }

  handleKeydown(event: KeyboardEvent): void {
    if (
      !this.disabled() &&
      this.clickable() &&
      (event.key === 'Enter' || event.key === ' ')
    ) {
      event.preventDefault();

      // Set clicking state for animation
      this.isClicking.set(true);

      // Convert KeyboardEvent to MouseEvent for consistency
      const mouseEvent = new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
      });
      this.clicked.emit(mouseEvent);

      // Reset the clicking state after a short delay
      setTimeout(() => {
        this.isClicking.set(false);
        if (event.target instanceof HTMLElement) {
          event.target.blur();
        }
      }, 150);
    }
  }

  cardClass(): string {
    const classes = [
      'm3-card',
      `size-${this.size()}`,
      `variant-${this.variant()}`,
    ];

    if (this.disabled()) {
      classes.push('disabled');
    }

    if (this.clickable()) {
      classes.push('clickable');
    }

    if (this.pressed() || this.isClicking()) {
      classes.push('pressed');
    }

    if (this.dragged()) {
      classes.push('dragged');
    }

    return classes.join(' ');
  }
}
